import React, { useState, useEffect, useRef } from 'react';
import { getSpriteUrl } from '../logic/assetUtils';
import Settings from '../components/Settings';
import AnimatedTitle from '../components/AnimatedTitle';
import { FallingLeaves } from '../common/Effects';
import { getGsapModifiers, AnimatedBackground } from '../common/AnimatedBackgrounds';
import { gsap } from 'gsap';

export default function Home({ gameLogic, onStartGame }) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const animatedBackgroundRef = useRef(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButtons(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (animatedBackgroundRef.current) {
      const imageRef = animatedBackgroundRef.current.imageRef;
      const modifiers = getGsapModifiers(animatedBackgroundRef);

      timelineRef.current = gsap.timeline();

      timelineRef.current.to(
        imageRef,
        {
          duration: 5,
          scale: 1.1,
          ease: 'power2.inOut',
          modifiers,
        },
        'start'
      );

      timelineRef.current.to(
        imageRef,
        {
          duration: 30,
          x: '-200px',
          ease: 'power1.inOut',
          repeat: -1,
          yoyo: true,
          modifiers,
        },
        'start+=2'
      );
    }

    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, []);

  const handleStartGame = () => {
    setButtonsDisabled(true);
    setIsTransitioning(true);

    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    setTimeout(() => {
      onStartGame();
    }, 1000);
  };

  const MenuButton = ({ onClick, disabled, delay, children }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative group px-12 py-3 transition-all duration-1000 w-[300px] 
        backdrop-blur-sm bg-white/5 border border-white/20 rounded-xl
        hover:border-white/40 hover:bg-white/10
        ${showButtons ? 'translate-x-0 opacity-100' : 'translate-x-[-100vw] opacity-0'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={{
        transitionDelay: `${delay}ms`,
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
        <div className="absolute inset-0 bg-gradient-to-r from-rose-300/20 via-pink-300/20 to-purple-300/20 blur-sm" />
      </div>
      <div className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
        <div className="absolute inset-0 bg-gradient-to-r from-rose-500/20 via-pink-500/20 to-purple-500/20 blur-[2px]" />
      </div>
      <span className="relative font-['Playfair_Display'] font-semibold text-xl text-white/90 tracking-wider">
        {children}
      </span>
    </button>
  );

  return (
    <div className="w-screen h-screen relative overflow-hidden">
      {/* Background Layer - z-0 */}
      <AnimatedBackground
        ref={animatedBackgroundRef}
        imageSrc={getSpriteUrl('homePageBackground')}
      />
      <div className="absolute inset-0 bg-black/20 pointer-events-none" />

      {/* Decorative Layer - z-10 */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent via-rose-500/5 to-transparent" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-rose-300 rounded-full filter blur-[180px] opacity-20 animate-float" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-300 rounded-full filter blur-[180px] opacity-20 animate-float-delayed" />
      </div>

      {/* Particle Effects - z-15 */}
      <div className="absolute inset-0 z-[15] pointer-events-none">
        <FallingLeaves color="#ffd7e4" count={15} />
        <FallingLeaves color="#ffe4e8" count={15} />
      </div>

      {!showSettings && (
        <>
          {/* Title Layer - z-20 */}
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-start pt-20 pointer-events-none">
            <div className="w-[400px] h-[200px]">
              <AnimatedTitle />
            </div>
          </div>

          {/* Interactive Layer - z-30 */}
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-full h-full bg-[radial-gradient(circle,rgba(0,0,0,0.3)_0%,rgba(0,0,0,0.1)_40%,transparent_70%)]" />
            </div>

            <div className="flex flex-col gap-4 mt-32">
              <MenuButton onClick={handleStartGame} disabled={buttonsDisabled} delay={0}>
                NEW GAME
              </MenuButton>
              
              <MenuButton disabled={buttonsDisabled} delay={100}>
                LOAD
              </MenuButton>
              
              <MenuButton onClick={() => setShowSettings(true)} disabled={buttonsDisabled} delay={200}>
                SETTINGS
              </MenuButton>
              
              <MenuButton disabled={buttonsDisabled} delay={300}>
                QUIT
              </MenuButton>
            </div>
          </div>
        </>
      )}

      {/* Settings Modal Layer - z-40 */}
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}

      {/* Transition Layer - z-50 */}
      {isTransitioning && (
        <div
          className="absolute inset-0 z-50 bg-white transition-all duration-1000 ease-in-out"
          style={{
            opacity: isTransitioning ? 1 : 0,
            filter: 'brightness(1.5) blur(20px)',
          }}
        />
      )}

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(20px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 7s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
